/**
 * This page is setup to allow for dynamic nested page pages from DatoCMS
 * It will build all pages and their children
 * It will also build the parent pages for each child page
 * This is to allow for a page to be found at /parent/child
 *
 * Simply reorganise the pages in DatoCMS to change the URL structure
 * https://www.datocms.com/docs/content-modelling/trees
 */

import { join, map } from 'lodash';
import { GetStaticProps } from 'next';
import { useRouter } from 'next/router';
import type { ReactElement } from 'react';
import { useQuerySubscription } from 'react-datocms';

import { ContentMatrix } from '@/components/content-matrix';
import { PageLayout } from '@/components/layouts/page-layout';
import { PageLoading } from '@/components/page-loading';
import { createDatoSubscription } from '@/lib/api/utils/create-dato-subscription';
import { PAGE_REVALIDATE_INTERVAL_WEEK, POST_TYPES } from '@/lib/constants';
import { AllPagesPageInterface, DatoQueryListenerOptions } from '@/types/cms-types';
import { cmsService } from '@/utils/api/services/cms.service';

type PageProps = { subscription: DatoQueryListenerOptions };

const Page = ({ subscription }: PageProps): ReactElement => {
  const {
    data: { page },
    status,
    error,
  } = useQuerySubscription(subscription);

  const router = useRouter();
  // If the page is not yet generated, this will be displayed
  // initially until getStaticProps() finishes running
  if (router.isFallback) {
    return <PageLoading />;
  }

  return (
    <PageLayout
      metaTags={page?.seoMetaTags}
      previewStatus={{ status, error }}
      canonical={page.canonical}
      posts={page.posts}
    >
      <div className="blocks">
        <ContentMatrix blocks={page?.blocks} />
      </div>
    </PageLayout>
  );
};

export const getStaticProps: GetStaticProps = async ({ params = {}, preview = false }) => {
  // getStaticPaths requires slug to be an array, the query
  // to Dato requires this to be a string
  const slugString = join(params.slug, '/');
  const page = await cmsService.page.getOne(slugString, preview);

  if (!page || !page.slug) {
    return {
      notFound: true,
    };
  }

  return {
    props: {
      subscription: await createDatoSubscription({ page, slug: slugString, preview }),
    },
    revalidate: PAGE_REVALIDATE_INTERVAL_WEEK,
  };
};

export async function getStaticPaths() {
  const allPages = await cmsService.page.getAll();

  // Hack to not build ports, contact page or post pages
  // I'm not sure of a nice way around this?
  const ignoreList = ['ports', 'contact', ...map(POST_TYPES)];

  const filteredPages = allPages.filter((p: { slug: string }) => !ignoreList.includes(p.slug));

  const paths = filteredPages.flatMap(({ slug, parent }: AllPagesPageInterface) => {
    if (!slug) throw new Error('Invalid slug');

    const slugs = slug.split('/');

    const parentSlugs = parent ? parent.slug.split('/') : [];
    const grandParentSlugs = parent?.parent ? parent.parent.slug.split('/') : [];

    // Combine the parent, grandparent, and child slugs
    const combinedSlugs = grandParentSlugs.concat(parentSlugs, slugs);

    return { params: { slug: combinedSlugs } };
  });

  return {
    paths,
    // NOTE: Added this to prevent the page from
    // being rendered when DatoCMS is disabled
    fallback: ['local', 'preview'].includes(process.env.NEXT_PUBLIC_ENVIRONMENT) ? 'blocking' : false,
  };
}

export default Page;
